/**
 * Shop This Drop - Main JavaScript
 * Handles gamified shopping experience, drag & drop, animations, and rewards
 */

class ShopThisDrop {
  constructor() {
    this.cart = {
      items: [],
      total: 0,
      itemCount: 0,
      milestones: {
        firstItem: false,
        threeItems: false,
        fiftyDollars: false,
        hundredDollars: false
      }
    };
    
    this.sounds = {
      addToCart: null,
      levelUp: null,
      success: null
    };
    
    this.init();
  }

  init() {
    this.loadSounds();
    this.initDragAndDrop();
    this.initCartFunctionality();
    this.initAnimations();
    this.initRewards();
    this.bindEvents();
    
    console.log('Shop This Drop initialized');
  }

  // Sound System
  loadSounds() {
    if (!window.shopifyThemeSettings?.enable_sound_effects) return;
    
    try {
      // Create audio context for better browser support
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Load sound files (you would need to upload these to assets)
      this.sounds.addToCart = this.createBeep(800, 0.1);
      this.sounds.levelUp = this.createBeep(1200, 0.2);
      this.sounds.success = this.createBeep(1000, 0.15);
    } catch (error) {
      console.log('Audio not supported:', error);
    }
  }

  createBeep(frequency, duration) {
    return () => {
      if (!this.audioContext) return;
      
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration);
    };
  }

  playSound(soundName) {
    if (this.sounds[soundName] && window.shopifyThemeSettings?.enable_sound_effects) {
      this.sounds[soundName]();
    }
  }

  // Drag and Drop System
  initDragAndDrop() {
    if (!window.shopifyThemeSettings?.enable_drag_drop) return;
    
    // Wait for DOM to be fully loaded, then re-initialize on dynamic content
    this.initDragAndDropCards();
    
    // Re-initialize when new content is loaded (pagination, filtering, etc.)
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.initDragAndDropCards(), 100);
    });
  }

  initDragAndDropCards() {
    // Create cart drop zone first
    this.ensureCartDropZone();
    
    // Note: Drag events are now handled inline in card-product.liquid
    // This avoids conflicts with the Refresh theme's interactive elements
    console.log('🎯 Drag and drop using INLINE implementation in product cards');
  }

  makeDraggableSimple(element) {
    // Skip if already processed
    if (element.hasAttribute('data-std-draggable')) return;
    element.setAttribute('data-std-draggable', 'true');
    
    // Only enable on desktop
    if (window.innerWidth >= 750) {
      element.draggable = true;
      
      element.addEventListener('dragstart', (e) => {
        console.log('🎯 Simple drag started for product:', element.dataset.productId);
        element.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        
        // Get variant ID from data attributes
        const variantId = element.dataset.variantId || 
                         element.querySelector('.card-variant-id')?.value ||
                         element.querySelector('[data-variant-id]')?.dataset.variantId;
        
        if (variantId) {
          e.dataTransfer.setData('text/plain', variantId);
          console.log('🎯 Variant ID set:', variantId);
        }
        
        // Show drop zone
        const dropZone = document.querySelector('.cart-drop-zone');
        if (dropZone) {
          dropZone.classList.add('visible', 'active');
        }
      });
      
      element.addEventListener('dragend', (e) => {
        console.log('🎯 Simple drag ended');
        element.classList.remove('dragging');
        
        // Hide drop zone after delay
        setTimeout(() => {
          const dropZone = document.querySelector('.cart-drop-zone');
          if (dropZone) {
            dropZone.classList.remove('visible', 'active');
          }
        }, 500);
      });
    }
  }

  makeDraggable(element) {
    // Skip if already made draggable
    if (element.hasAttribute('data-std-draggable')) return;
    element.setAttribute('data-std-draggable', 'true');
    
    // Only enable drag on desktop
    if (window.innerWidth >= 750) {
      // Create a dedicated drag handle
      const dragHandle = document.createElement('div');
      dragHandle.className = 'std-drag-handle';
      dragHandle.innerHTML = '⋮⋮';
      dragHandle.title = 'Drag to add to cart';
      element.appendChild(dragHandle);
      
      // Make the drag handle draggable, not the entire card
      dragHandle.draggable = true;
      element.draggable = false;
      
      // Prevent drag from starting on links, buttons, and interactive elements
      const interactiveElements = element.querySelectorAll('a, button, input, select, textarea, [role="button"]');
      interactiveElements.forEach(interactive => {
        interactive.draggable = false;
      });
      
      dragHandle.addEventListener('dragstart', (e) => {
        console.log('🎯 DRAGSTART EVENT FIRED from drag handle');
        console.log('🎯 Event target:', e.target);
        console.log('🎯 Parent element:', element);
        
        // Prevent any default behavior that might interfere
        e.stopPropagation();
        
        element.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        
          // Create drag preview that follows the mouse
          const dragPreview = element.cloneNode(true);
          dragPreview.classList.add('drag-preview');
          dragPreview.style.cssText = `
            position: fixed;
            pointer-events: none;
            z-index: 1001;
            opacity: 0.8;
            transform: rotate(-5deg) scale(0.8);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
            border-radius: 12px;
            transition: none !important;
            width: ${element.offsetWidth}px;
            height: ${element.offsetHeight}px;
          `;
          document.body.appendChild(dragPreview);
          
          // Position preview at mouse cursor
          const updatePreview = (e) => {
            if (dragPreview) {
              dragPreview.style.left = (e.clientX - dragPreview.offsetWidth / 2) + 'px';
              dragPreview.style.top = (e.clientY - dragPreview.offsetHeight / 2) + 'px';
            }
          };
          
          // Start following mouse immediately
          updatePreview(e);
          document.addEventListener('mousemove', updatePreview);
          
          // Clean up on drag end
          const cleanupPreview = () => {
            if (dragPreview) {
              dragPreview.remove();
              document.removeEventListener('mousemove', updatePreview);
            }
          };
          document.addEventListener('mouseup', cleanupPreview, { once: true });
          
          console.log('✅ Drag preview created and following mouse');
          
          // Store product info for drop handling
          const productForm = element.querySelector('product-form, form[action*="/cart/add"]');
          let variantId = null;
        
        // Try to get variant ID from various sources
        const variantInput = element.querySelector('input[name="id"], select[name="id"], .card-variant-id');
        if (variantInput) {
          variantId = variantInput.value;
        } else {
          // Check for variant ID in card data attributes (from variant picker)
          variantId = element.dataset.variantId;
          
          if (!variantId) {
            // For product cards without forms, try to get the first available variant
            const productId = element.dataset.productId || element.querySelector('[data-product-id]')?.dataset.productId;
            if (productId) {
              // For simple products, use the product ID (Shopify will use the first available variant)
              variantId = productId;
            } else {
              // Try to find variant ID in product form data or buttons
              const addButton = element.querySelector('button[name="add"], input[name="add"], .card-add-to-cart-btn');
              if (addButton) {
                variantId = addButton.dataset.variantId;
                if (!variantId && addButton.form) {
                  const formVariantInput = addButton.form.querySelector('input[name="id"], select[name="id"]');
                  if (formVariantInput) {
                    variantId = formVariantInput.value;
                  }
                }
              }
            }
          }
        }
        
        if (variantId) {
          e.dataTransfer.setData('text/plain', variantId);
        }
        
        console.log('Drag started for product:', e.dataTransfer.getData('text/plain'));
        
        // Hide drag hint after first drag
        const dragHint = document.querySelector('.drag-hint');
        if (dragHint) {
          dragHint.style.display = 'none';
          localStorage.setItem('std_drag_hint_shown', 'true');
        }
      });
      
      dragHandle.addEventListener('dragend', (e) => {
        console.log('🎯 DRAGEND EVENT FIRED from drag handle');
        element.classList.remove('dragging');
        this.removeDragPreview();
      });
    } else {
      // Mobile: use touch events for similar functionality
      element.draggable = false;
      this.addTouchSupport(element);
    }
  }

  addTouchSupport(element) {
    let touchStartTime = 0;
    let touchMoved = false;
    
    element.addEventListener('touchstart', (e) => {
      touchStartTime = Date.now();
      touchMoved = false;
    }, { passive: true });
    
    element.addEventListener('touchmove', (e) => {
      touchMoved = true;
    }, { passive: true });
    
    element.addEventListener('touchend', (e) => {
      const touchDuration = Date.now() - touchStartTime;
      
      // Long press (800ms) activates special mobile cart functionality
      if (touchDuration > 800 && !touchMoved) {
        e.preventDefault();
        this.handleMobileLongPress(element);
      }
    });
  }

  handleMobileLongPress(element) {
    // Mobile long-press alternative to drag & drop
    const productCard = element.querySelector('.card');
    const addButton = productCard?.querySelector('product-form button[type="submit"]');
    
    if (addButton && !addButton.disabled) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Show mobile-specific animation
      element.classList.add('mobile-selected');
      setTimeout(() => {
        element.classList.remove('mobile-selected');
      }, 300);
      
      // Trigger the add to cart
      addButton.click();
      this.showNotification('Added via long press! 📱', 'success');
    }
  }

  ensureCartDropZone() {
    // First, check if we should show the drop zone on this page
    const currentPath = window.location.pathname;
    const allowedPaths = [
      '/',                          // Home page
      '/collections/all',           // All collections
      '/collections/',              // Any collection page
      '/pages/past-drops',          // Past drops page
      '/products/'                  // Any product page
    ];
    
    // Only show on specific pages and not on mobile
    const shouldShowDropZone = 
      window.innerWidth >= 750 && ( // Only show on desktop/tablet
        currentPath === '/collections/all' || // All collections page
        (currentPath.startsWith('/collections/') && currentPath !== '/collections/all/products') || // Collection pages but not "all products"
        currentPath === '/pages/past-drops' // Past drops page
      );
    
    if (!shouldShowDropZone) {
      // Remove drop zone if it exists
      const existingDropZone = document.querySelector('.cart-drop-zone');
      if (existingDropZone) {
        existingDropZone.remove();
      }
      return;
    }
    
    // Ensure styles are loaded
    this.ensureDropZoneStyles();
    
    let dropZone = document.querySelector('.cart-drop-zone');
    if (!dropZone) {
      dropZone = document.createElement('div');
      dropZone.className = 'cart-drop-zone';
      dropZone.innerHTML = `
        <div class="cart-drop-zone-icon">🛒</div>
        <div class="cart-drop-zone-text">Drop Here</div>
      `;
      dropZone.title = 'Drop products here to add to cart';
      document.body.appendChild(dropZone);
      
      // Make it visible immediately and permanently
      dropZone.style.transform = 'scale(1) translateY(0)';
      
      console.log('Cart drop zone created:', dropZone);
    }
    this.initDropZone(dropZone);
    
    // OLD HTML5 drag events disabled - using mouse-based system instead
    console.log('🎯 Mouse-based drag system active - no HTML5 event listeners needed');
  }

  ensureDropZoneStyles() {
    // Only add styles once
    if (document.getElementById('std-dragdrop-styles')) return;
    
    const styles = document.createElement('style');
    styles.id = 'std-dragdrop-styles';
    styles.textContent = `
      @keyframes slideInRight {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      .mint-glow-strong {
        box-shadow: 0 0 20px rgba(74, 222, 128, 0.6);
        transform: scale(1.02);
      }
      .animate-slide-up {
        animation: slideUp 0.6s ease forwards;
      }
      @keyframes slideUp {
        from { transform: translateY(30px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      .mobile-selected {
        animation: mobile-pulse 0.3s ease;
      }
      @keyframes mobile-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); background-color: rgba(74, 222, 128, 0.1); }
      }
      /* Enhanced Cart Drop Zone */
      .cart-drop-zone {
        position: fixed;
        bottom: 40px;
        left: 40px;
        width: 220px;
        height: 220px;
        background: #98D47E;
        border-radius: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: white;
        box-shadow: 0 15px 35px rgba(152, 212, 126, 0.4),
                    0 5px 15px rgba(0, 0, 0, 0.1);
        transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        opacity: 0.85;
        pointer-events: all;
        z-index: 1000;
        border: 4px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(12px);
        transform: scale(0.9) translateY(20px);
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
      }
      
      .cart-drop-zone.visible {
        opacity: 1 !important;
        transform: scale(1.2) translateY(0) !important;
        pointer-events: all !important;
        display: flex !important;
        box-shadow: 0 15px 40px rgba(152, 212, 126, 0.8) !important;
      }
      
      .cart-drop-zone.active {
        transform: scale(1.3) translateY(-10px);
        box-shadow: 0 15px 40px rgba(152, 212, 126, 0.6);
        opacity: 1;
        background: #98D47E;
        border-color: rgba(255, 255, 255, 0.6);
      }
      
      .cart-drop-zone.drag-over {
        transform: scale(1.5) translateY(-15px) !important;
        box-shadow: 0 25px 50px rgba(152, 212, 126, 0.8) !important;
        background: #98D47E !important;
        border-color: rgba(255, 255, 255, 0.9) !important;
        animation: dragOverPulse 0.5s ease-in-out infinite alternate;
      }
      
      @keyframes dragOverPulse {
        from { opacity: 0.9; }
        to { opacity: 1; }
      }
      
      .cart-drop-zone-icon {
        font-size: 64px;
        margin-bottom: 16px;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        animation: float 3s ease-in-out infinite;
      }

      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-6px); }
      }

      .cart-drop-zone-text {
        font-size: 18px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        opacity: 1;
        color: white;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        padding: 8px 16px;
        border-radius: 12px;
        backdrop-filter: blur(4px);
      }

      @media screen and (max-width: 749px) {
        .cart-drop-zone {
          width: 150px;
          height: 150px;
        }
        .cart-drop-zone-icon {
          font-size: 48px;
          margin-bottom: 8px;
        }
        .cart-drop-zone-text {
          font-size: 14px;
          padding: 4px 12px;
        }
      }
      
      /* Enhanced Dragging States */
      .dragging {
        opacity: 0.8 !important;
        transform: rotate(5deg) scale(1.1) !important;
        z-index: 1000 !important;
        pointer-events: none !important;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4) !important;
        border: 2px solid rgba(16, 185, 129, 0.5) !important;
        filter: brightness(1.2) !important;
        transition: none !important;
      }
      
      /* Dedicated drag handle styling */
      .std-drag-handle {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        background: rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        color: rgba(0, 0, 0, 0.6);
        font-size: 14px;
        line-height: 24px;
        text-align: center;
        cursor: grab;
        z-index: 10;
        transition: all 0.2s;
        user-select: none;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .std-drag-handle:hover {
        background: rgba(0, 0, 0, 0.2);
        color: rgba(0, 0, 0, 0.8);
        transform: scale(1.1);
      }
      
      .std-drag-handle:active {
        cursor: grabbing;
        background: rgba(16, 185, 129, 0.2);
        color: rgb(16, 185, 129);
      }
      
      .card-wrapper[data-std-draggable="true"] {
        position: relative;
      }
      
      .drag-preview {
        position: fixed;
        pointer-events: none;
        z-index: 1001;
        opacity: 0.8;
        transform: rotate(-5deg) scale(0.8);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
        border-radius: 12px;
        transition: none !important;
      }
      
      /* Drag hint animation */
      .drag-hint {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 16px 24px;
        border-radius: 12px;
        font-size: 16px;
        font-weight: 500;
        z-index: 1002;
        opacity: 0;
        pointer-events: none;
        backdrop-filter: blur(10px);
        animation: fadeInOut 3s ease-in-out;
        border: 2px solid rgba(16, 185, 129, 0.5);
      }
      
      @keyframes fadeInOut {
        0%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
        20%, 80% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      }
      
      /* Pulse effect when hovering over drop zone */
      .cart-drop-zone.pulse {
        animation: dropZonePulse 0.6s ease-in-out;
      }
      
      @keyframes dropZonePulse {
        0%, 100% { transform: scale(1.3) translateY(-10px); }
        50% { transform: scale(1.4) translateY(-15px); }
      }
    `;
    document.head.appendChild(styles);
    console.log('Drop zone styles added');
  }

  showDropZoneOnDrag() {
    const dropZone = document.querySelector('.cart-drop-zone');
    if (!dropZone) return;

    // Show drop zone when dragging starts
    document.addEventListener('dragstart', (e) => {
      console.log('Drag start detected, showing drop zone');
      dropZone.classList.add('visible');
      
      // Force inline styles as backup
      dropZone.style.opacity = '1';
      dropZone.style.transform = 'scale(1.2) translateY(0)';
      dropZone.style.pointerEvents = 'all';
      dropZone.style.display = 'flex';
      dropZone.style.boxShadow = '0 15px 40px rgba(16, 185, 129, 0.8)';
      
      this.showDragHint();
      
      // Force visibility for debugging
      setTimeout(() => {
        console.log('Drop zone classes:', dropZone.className);
        console.log('Drop zone style:', dropZone.style.cssText);
        console.log('Drop zone computed style opacity:', getComputedStyle(dropZone).opacity);
        console.log('Drop zone computed style display:', getComputedStyle(dropZone).display);
        console.log('Drop zone computed style transform:', getComputedStyle(dropZone).transform);
      }, 100);
    });

    // Hide drop zone when dragging ends
    document.addEventListener('dragend', (e) => {
      console.log('Drag end detected');
      // Delay hiding to allow for drop events to process
      setTimeout(() => {
        dropZone.classList.remove('visible', 'active', 'pulse');
        dropZone.style.opacity = '';
        dropZone.style.transform = '';
        dropZone.style.pointerEvents = '';
        dropZone.style.display = '';
        console.log('Drop zone hidden');
      }, 1000);
    });
    
    // Also listen for document drag events to keep drop zone visible
    document.addEventListener('dragover', (e) => {
      if (e.target !== dropZone && !dropZone.contains(e.target)) {
        // Keep drop zone visible during drag over other elements
        if (!dropZone.classList.contains('visible')) {
          dropZone.classList.add('visible');
          dropZone.style.opacity = '0.9';
          dropZone.style.transform = 'scale(1) translateY(0)';
          dropZone.style.pointerEvents = 'all';
          dropZone.style.display = 'flex';
        }
      }
    });
  }

  showDragHint() {
    // Show hint only once per session
    if (localStorage.getItem('std_drag_hint_shown')) return;

    const hint = document.createElement('div');
    hint.className = 'drag-hint';
    hint.textContent = 'Drag products to the cart icon to add them!';
    document.body.appendChild(hint);

    setTimeout(() => {
      hint.remove();
      localStorage.setItem('std_drag_hint_shown', 'true');
    }, 3000);
  }

  createDragPreview(element, dragEvent) {
    // Set custom drag image to empty for better control
    const dragImage = document.createElement('div');
    dragImage.style.cssText = `
      width: 1px;
      height: 1px;
      opacity: 0;
      position: absolute;
      top: -1000px;
    `;
    document.body.appendChild(dragImage);
    dragEvent.dataTransfer.setDragImage(dragImage, 0, 0);
    
    // Remove empty drag image after a moment
    setTimeout(() => dragImage.remove(), 100);
    
    console.log('Drag preview set for element:', element);
  }

  removeDragPreview() {
    const preview = document.querySelector('.drag-preview');
    if (preview) {
      // Remove the event listener
      const updatePreview = () => {}; // Placeholder
      document.removeEventListener('dragover', updatePreview);
      preview.remove();
    }
  }

  initDropZone(dropZone) {
    // DISABLED: HTML5 drag and drop system interferes with mouse-based system
    // Mouse-based drag is handled inline in product cards
    console.log('🚫 HTML5 drop zone disabled - using mouse-based drag system');
  }

  showDropSuccessAnimation(dropZone) {
    // Change icon temporarily to show success
    const icon = dropZone.querySelector('.cart-drop-zone-icon');
    const text = dropZone.querySelector('.cart-drop-zone-text');
    
    if (icon && text) {
      const originalIcon = icon.textContent;
      const originalText = text.textContent;
      
      icon.textContent = '✓';
      text.textContent = 'Added!';
      dropZone.style.background = 'linear-gradient(135deg, #059669, #047857)';
      
      setTimeout(() => {
        icon.textContent = originalIcon;
        text.textContent = originalText;
        dropZone.style.background = '';
      }, 1500);
    }
  }

  // Update Refresh theme's cart icon specifically  
  async updateRefreshThemeCart(cartData) {
    const cartIconBubble = document.querySelector('#cart-icon-bubble');
    if (!cartIconBubble) {
      console.log('❌ Cart icon bubble not found');
      return;
    }

    console.log('🔄 Updating Refresh theme cart, item count:', cartData.item_count);

    if (cartData.item_count > 0) {
      // Cart has items - ensure cart count bubble exists and is updated
      let cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      
      if (!cartCountBubble) {
        // Create cart count bubble if it doesn't exist
        cartCountBubble = document.createElement('div');
        cartCountBubble.className = 'cart-count-bubble';
        cartIconBubble.appendChild(cartCountBubble);
        console.log('✅ Created cart count bubble');
      }

      // Update the count number
      let countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
      if (!countSpan) {
        countSpan = document.createElement('span');
        countSpan.setAttribute('aria-hidden', 'true');
        cartCountBubble.appendChild(countSpan);
      }
      
      countSpan.textContent = cartData.item_count;
      
      // Update the visually hidden count text
      let hiddenSpan = cartCountBubble.querySelector('.visually-hidden');
      if (!hiddenSpan) {
        hiddenSpan = document.createElement('span');
        hiddenSpan.className = 'visually-hidden';
        cartCountBubble.appendChild(hiddenSpan);
      }
      hiddenSpan.textContent = cartData.item_count === 1 ? '1 item' : `${cartData.item_count} items`;

      // Switch cart icon from empty to full
      const iconWrapper = cartIconBubble.querySelector('.svg-wrapper');
      if (iconWrapper && iconWrapper.innerHTML.includes('icon-cart-empty')) {
        // Use inline SVG for full cart icon
        const fullCartIcon = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart" viewBox="0 0 40 40"><path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"/></svg>';
        iconWrapper.innerHTML = fullCartIcon;
        console.log('✅ Switched to full cart icon');
      }

      // Add bounce animation
      cartCountBubble.style.transform = 'scale(1.5)';
      cartCountBubble.style.transition = 'transform 0.2s ease';
      setTimeout(() => {
        cartCountBubble.style.transform = 'scale(1)';
      }, 200);

      console.log('✅ Cart count updated to:', cartData.item_count);
      
    } else {
      // Cart is empty - remove count bubble if it exists
      const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      if (cartCountBubble) {
        cartCountBubble.remove();
        console.log('✅ Removed cart count bubble (cart empty)');
      }

      // Switch to empty cart icon
      const iconWrapper = cartIconBubble.querySelector('.svg-wrapper');
      if (iconWrapper && !iconWrapper.innerHTML.includes('icon-cart-empty')) {
        // Use inline SVG for empty cart icon
        const emptyCartIcon = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart-empty" viewBox="0 0 40 40"><path fill="currentColor" fill-rule="evenodd" d="M15.75 11.8h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33L28.4 11.8zm0 1h-2.22l-.71 10.67a4 4 0 0 0 3.99 4.27h7.38a4 4 0 0 0 4-4.27l-.72-10.67h-2.22v.63a4.75 4.75 0 1 1-9.5 0zm8.5 0h-7.5v.63a3.75 3.75 0 1 0 7.5 0z"/></svg>';
        iconWrapper.innerHTML = emptyCartIcon;
        console.log('✅ Switched to empty cart icon');
      }
    }
  }

  // Force refresh Shopify theme's cart UI
  async refreshShopifyCartUI(cartData) {
    try {
      console.log('🔄 Refreshing Shopify cart UI...');
      
      // Method 1: Fetch fresh cart data and update cart count
      const cartResponse = await fetch('/cart.js');
      const freshCartData = await cartResponse.json();
      
      // Update Refresh theme's cart icon specifically
      await this.updateRefreshThemeCart(freshCartData);
      
      // Method 2: Force cart drawer section refresh using Shopify's section rendering
      console.log('🔄 Forcing cart drawer section refresh...');
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        try {
          // Fetch fresh cart drawer HTML and update it
          const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
          const html = await response.text();
          const parser = new DOMParser();
          const newDoc = parser.parseFromString(html, 'text/html');
          
          // Get the entire cart drawer HTML
          const newCartDrawerContent = newDoc.querySelector('cart-drawer');
          if (!newCartDrawerContent) {
            console.error('❌ Could not find new cart drawer content');
            return;
          }

          // Preserve the current drawer's event listeners and properties
          const currentDrawerProps = {
            activeElement: cartDrawer.activeElement,
            hasAttribute: (name) => cartDrawer.hasAttribute(name),
            getAttribute: (name) => cartDrawer.getAttribute(name)
          };

          // Replace the entire drawer contents
          cartDrawer.innerHTML = newCartDrawerContent.innerHTML;
          console.log('✅ Cart drawer contents fully replaced');

          // Re-initialize the drawer's functionality
          const cartDrawerForm = cartDrawer.querySelector('#CartDrawer-Form');
          if (cartDrawerForm) {
            // Re-initialize quantity inputs
            cartDrawerForm.querySelectorAll('.quantity__input').forEach(input => {
              input.addEventListener('change', (event) => {
                event.preventDefault();
                const cartItems = input.closest('cart-drawer-items');
                if (cartItems) {
                  cartItems.validateQuantity(event);
                }
              });
            });

            // Re-initialize remove buttons
            cartDrawerForm.querySelectorAll('cart-remove-button').forEach(button => {
              button.addEventListener('click', (event) => {
                event.preventDefault();
                const cartItems = button.closest('cart-drawer-items');
                if (cartItems) {
                  cartItems.updateQuantity(button.dataset.index, 0, event);
                }
              });
            });
          }

          // Update cart drawer class to remove is-empty
          cartDrawer.classList.remove('is-empty');

          // Re-initialize cart note if present
          const cartNote = cartDrawer.querySelector('cart-note');
          if (cartNote) {
            cartNote.addEventListener('input', (event) => {
              const body = JSON.stringify({ note: event.target.value });
              fetch(`${window.routes.cart_update_url}`, { 
                ...fetchConfig(), 
                ...{ body } 
              });
            });
          }

          // Re-initialize checkout button and form
          const cartForm = cartDrawer.querySelector('#CartDrawer-Form');
          if (cartForm) {
            // Remove any existing event listeners
            const newForm = cartForm.cloneNode(true);
            cartForm.parentNode.replaceChild(newForm, cartForm);
            
            // Initialize checkout button
            const checkoutButton = newForm.querySelector('#CartDrawer-Checkout');
            if (checkoutButton) {
              checkoutButton.addEventListener('click', (event) => {
                event.preventDefault();
                console.log('🛒 Checkout button clicked, submitting form...');
                
                // Create a hidden input for the checkout action
                const checkoutInput = document.createElement('input');
                checkoutInput.type = 'hidden';
                checkoutInput.name = 'checkout';
                checkoutInput.value = '1';
                newForm.appendChild(checkoutInput);
                
                // Submit the form
                newForm.submit();
              });
            }
            
            // Initialize the form itself
            newForm.addEventListener('submit', (event) => {
              if (!event.submitter || event.submitter.name !== 'checkout') {
                event.preventDefault();
              }
            });
          }

          console.log('✅ Cart drawer functionality re-initialized');
          
          // Force open the cart drawer to show the updated content
          console.log('🔄 Opening cart drawer to show updated content...');
          
          // Add required classes and styles
          cartDrawer.style.visibility = 'visible';
          cartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          
          // Set up focus trapping
          const containerToTrapFocusOn = cartDrawer.classList.contains('is-empty')
            ? cartDrawer.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
            
          const focusElement = cartDrawer.querySelector('.drawer__inner') || cartDrawer.querySelector('.drawer__close');
          
          if (typeof trapFocus === 'function') {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
          
          // Add transition end listener
          cartDrawer.addEventListener('transitionend', () => {
            if (focusElement) focusElement.focus();
          }, { once: true });
          
          // Set up close button and overlay
          const closeButton = cartDrawer.querySelector('.drawer__close');
          const overlay = cartDrawer.querySelector('.cart-drawer__overlay');
          
          const closeDrawer = () => {
            cartDrawer.classList.remove('animate', 'active');
            cartDrawer.style.visibility = 'hidden';
            document.body.classList.remove('overflow-hidden');
            if (typeof removeTrapFocus === 'function') {
              removeTrapFocus(focusElement);
            }
            // Remove any stray overlays
            const existingOverlays = document.querySelectorAll('.cart-drawer__overlay');
            existingOverlays.forEach(overlay => {
              if (overlay.style.opacity !== '0') {
                overlay.style.opacity = '0';
                overlay.style.visibility = 'hidden';
              }
            });
            cartDrawer.dispatchEvent(new CustomEvent('cart:closed'));
          };
          
          // Handle close button click
          if (closeButton) {
            closeButton.onclick = (e) => {
              e.preventDefault();
              closeDrawer();
            };
          }
          
          // Handle overlay click
          if (overlay) {
            overlay.onclick = () => closeDrawer();
            // Ensure overlay is properly styled
            overlay.style.transition = 'opacity 0.3s ease-out';
          }
          
          // Handle Escape key
          const handleEscape = (e) => {
            if (e.code === 'Escape') {
              closeDrawer();
              document.removeEventListener('keyup', handleEscape);
            }
          };
          document.addEventListener('keyup', handleEscape);
          
          console.log('✅ Cart drawer opened to display updated items');
          
        } catch (error) {
          console.error('Error refreshing cart drawer:', error);
        }
      }
      
      // Method 3: Try to use global publish function if available
      console.log('📢 Checking for global publish function...');
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'shop-this-drop',
          cartData: freshCartData,
          variantId: cartData.variant_id
        });
        console.log('✅ Published PUB_SUB_EVENTS.cartUpdate via global function');
      } else {
        console.log('❌ Global publish function not available yet');
      }
      
      // Method 4: Dispatch DOM events (legacy support)
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: freshCartData }
      }));
      
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { 
          variant: cartData.variant_id, 
          quantity: cartData.quantity,
          cart: freshCartData 
        }
      }));
      
      // Method 5: Show cart icon if it was hidden
      const cartIcons = document.querySelectorAll('.cart-icon, [data-cart-icon]');
      cartIcons.forEach(icon => {
        icon.style.visibility = 'visible';
        icon.style.opacity = '1';
      });
      
      console.log('✅ Cart UI refreshed, item count:', freshCartData.item_count);
      
    } catch (error) {
      console.error('Error refreshing cart UI:', error);
    }
  }

  // Cart API method for drag & drop
  async addToCartViaAPI(variantId, productCard = null) {
    try {
      console.log('Adding to cart via API:', variantId);
      
      if (!variantId) {
        throw new Error('No variant ID provided');
      }
      
      // Validate that variantId is a valid numeric ID, not a URL
      const cleanVariantId = variantId.toString().trim();
      if (cleanVariantId.includes('http') || cleanVariantId.includes('/')) {
        throw new Error(`Invalid variant ID (appears to be URL): ${cleanVariantId}`);
      }
      
      // Ensure it's numeric
      if (!/^\d+$/.test(cleanVariantId)) {
        throw new Error(`Invalid variant ID (not numeric): ${cleanVariantId}`);
      }
      
      const formData = {
        items: [{
          id: cleanVariantId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.description || errorData.message || 'Failed to add to cart');
      }

      const data = await response.json();
      
      // Play sound and show animation
      this.playSound('addToCart');
      
      if (productCard) {
        this.animateProductCard(productCard);
      }
      
      // Update cart state and UI
      this.updateCartFromDOM();
      this.checkMilestones();
      
      // Show notification
      const productName = data.product_title || data.title || 'Product';
      this.showNotification(`${productName} added to cart! 🛒`, 'success');
      
      // Force update Shopify cart UI in multiple ways
      await this.refreshShopifyCartUI(data);
      
      // Create confetti for first item
      if (this.cart.itemCount === 0) {
        setTimeout(() => this.createConfetti(), 200);
      }
      
    } catch (error) {
      console.error('Error adding to cart via API:', error);
      this.showNotification(error.message || 'Error adding product to cart', 'error');
    }
  }

  // Cart Functionality
  initCartFunctionality() {
    // Listen for Refresh theme's cart events
    document.addEventListener('variant:add', (event) => {
      this.handleCartAdd(event);
    });
    
    document.addEventListener('cart:updated', (event) => {
      this.handleCartUpdate(event);
    });

    // Listen for card variant changes to update drag & drop
    document.addEventListener('card:variant:change', (event) => {
      this.handleCardVariantChange(event);
    });
  }

  handleCartAdd(event) {
    // Play sound
    this.playSound('addToCart');
    
    // Find the product card that was added
    const productCard = event.target.closest('.card-wrapper');
    if (productCard) {
      this.animateProductCard(productCard);
    }
    
    // Update cart state
    setTimeout(() => {
      this.updateCartFromDOM();
      this.checkMilestones();
    }, 100);
    
    // Create confetti effect for first item
    if (this.cart.itemCount === 0) { // Will be 1 after update
      setTimeout(() => this.createConfetti(), 200);
    }
  }

  handleCartUpdate(event) {
    this.updateCartFromDOM();
  }

  handleCardVariantChange(event) {
    const { variant, cardElement } = event.detail;
    
    // Update drag & drop data for the card
    const cardWrapper = cardElement.closest('.card-wrapper');
    if (cardWrapper && cardWrapper.hasAttribute('data-std-draggable')) {
      // Update the draggable data to use the new variant ID
      cardWrapper.setAttribute('data-variant-id', variant.id);
      
      // Ensure card is still draggable
      this.makeDraggable(cardWrapper);
    }
    
    console.log('Updated drag & drop for variant:', variant.id);
  }

  updateCartFromDOM() {
    // Get cart data from Refresh theme's cart drawer or header
    const cartCountElement = document.querySelector('.cart-count-bubble span, [data-cart-count]');
    const cartTotalElement = document.querySelector('[data-cart-total]');
    
    if (cartCountElement) {
      this.cart.itemCount = parseInt(cartCountElement.textContent) || 0;
    }
    
    if (cartTotalElement) {
      const totalText = cartTotalElement.textContent;
      const totalMatch = totalText.match(/[\d,.]+/);
      if (totalMatch) {
        this.cart.total = parseFloat(totalMatch[0].replace(',', '')) || 0;
      }
    }
  }

  // Animation System
  initAnimations() {
    if (!window.shopifyThemeSettings?.enable_animations) return;
    
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-up');
        }
      });
    }, observerOptions);
    
    // Observe elements for animation
    const animateElements = document.querySelectorAll('.card-wrapper, .card, .section-header');
    animateElements.forEach(el => observer.observe(el));
  }

  animateProductCard(card) {
    // Create dramatic animation sequence
    const timeline = [
      { transform: 'scale(0.8)', opacity: '0.5' },
      { transform: 'scale(1.1)', opacity: '1' },
      { transform: 'scale(1)', opacity: '1' }
    ];
    
    const options = {
      duration: 600,
      easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    };
    
    card.animate(timeline, options);
    
    // Add glow effect temporarily
    card.classList.add('mint-glow-strong');
    setTimeout(() => {
      card.classList.remove('mint-glow-strong');
    }, 1000);
  }

  // Rewards System
  initRewards() {
    if (!window.shopifyThemeSettings?.enable_rewards) return;
    
    // Load saved milestones from localStorage
    const savedMilestones = localStorage.getItem('std_milestones');
    if (savedMilestones) {
      this.cart.milestones = JSON.parse(savedMilestones);
    }
  }

  checkMilestones() {
    const previousMilestones = { ...this.cart.milestones };
    
    // Update milestones
    this.cart.milestones.firstItem = this.cart.itemCount >= 1;
    this.cart.milestones.threeItems = this.cart.itemCount >= 3;
    this.cart.milestones.fiftyDollars = this.cart.total >= 50;
    this.cart.milestones.hundredDollars = this.cart.total >= 100;
    
    // Check for new achievements
    Object.keys(this.cart.milestones).forEach(milestone => {
      if (!previousMilestones[milestone] && this.cart.milestones[milestone]) {
        this.celebrateMilestone(milestone);
      }
    });
    
    // Save milestones
    localStorage.setItem('std_milestones', JSON.stringify(this.cart.milestones));
  }

  celebrateMilestone(milestone) {
    this.playSound('levelUp');
    
    const messages = {
      firstItem: 'First item added! 🎉',
      threeItems: 'Three items milestone! 🔥',
      fiftyDollars: '$50 milestone reached! 💰',
      hundredDollars: '$100 milestone achieved! 🏆'
    };
    
    this.showNotification(messages[milestone], 'success');
    
    // Create confetti effect
    this.createConfetti();
  }

  createConfetti() {
    const colors = ['#4ade80', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];
    const confettiCount = 50;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.style.cssText = `
        position: fixed;
        width: 10px;
        height: 10px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        left: ${Math.random() * 100}vw;
        top: -10px;
        z-index: 1000;
        pointer-events: none;
        border-radius: 50%;
      `;
      
      document.body.appendChild(confetti);
      
      // Animate confetti falling
      confetti.animate([
        { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
        { transform: `translateY(100vh) rotate(720deg)`, opacity: 0 }
      ], {
        duration: Math.random() * 2000 + 1000,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }).onfinish = () => confetti.remove();
    }
  }

  // Utility Functions
  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `std-notification std-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#10b981' : '#ef4444'};
      color: white;
      padding: 1rem 1.5rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 1000;
      font-weight: 500;
      max-width: 300px;
      animation: slideInRight 0.3s ease;
    `;
    
    // Ensure drop zone styles are loaded (moved to ensureDropZoneStyles)
    this.ensureDropZoneStyles();
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideInRight 0.3s ease reverse';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // Event Binding
  bindEvents() {
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateCartFromDOM();
      }
    });
    
    // Handle window resize
    window.addEventListener('resize', this.debounce(() => {
      this.handleResize();
    }, 250));
    
    // Initialize keyboard shortcuts
    this.initKeyboardShortcuts();
  }


  // Keyboard shortcuts
  initKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Toggle drag hints with 'H' key
      if (e.key.toLowerCase() === 'h' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.toggleDragHints();
      }
    });
  }

  toggleDragHints() {
    const hints = document.querySelectorAll('.drag-hint');
    hints.forEach(hint => {
      hint.classList.toggle('show');
    });
  }

  handleResize() {
    // Adjust functionality for mobile
    if (window.innerWidth < 750) {
      const dropZone = document.querySelector('.cart-drop-zone');
      if (dropZone) {
        dropZone.style.display = 'none';
      }
    } else {
      const dropZone = document.querySelector('.cart-drop-zone');
      if (dropZone) {
        dropZone.style.display = 'flex';
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

  // Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Delay initialization slightly to ensure Refresh theme is fully loaded
  setTimeout(() => {
    window.shopThisDrop = new ShopThisDrop();
  }, 100);
});

// Also check on page navigation (for client-side routing)
window.addEventListener('popstate', () => {
  if (window.shopThisDrop) {
    window.shopThisDrop.ensureCartDropZone();
  }
});

// Check on Shopify page transitions
document.addEventListener('shopify:section:load', () => {
  if (window.shopThisDrop) {
    window.shopThisDrop.ensureCartDropZone();
  }
});

// Also initialize on window load as backup
window.addEventListener('load', () => {
  if (!window.shopThisDrop) {
    window.shopThisDrop = new ShopThisDrop();
  }
});

// Export for use in other scripts
window.ShopThisDrop = ShopThisDrop;

// Debug helper functions
window.reInitShopThisDrop = () => {
  console.log('Re-initializing Shop This Drop...');
  if (window.shopThisDrop) {
    window.shopThisDrop.initDragAndDropCards();
  } else {
    window.shopThisDrop = new ShopThisDrop();
  }
};

window.testDropZone = () => {
  console.log('Testing drop zone...');
  if (window.shopThisDrop) {
    // Force create drop zone
    window.shopThisDrop.ensureCartDropZone();
    
    // Make it visible
    const dropZone = document.querySelector('.cart-drop-zone');
    if (dropZone) {
      dropZone.classList.add('visible');
      
      // Force inline styles aggressively
      dropZone.style.opacity = '0.9';
      dropZone.style.transform = 'scale(1) translateY(0)';
      dropZone.style.pointerEvents = 'all';
      dropZone.style.display = 'flex';
      dropZone.style.visibility = 'visible';
      dropZone.style.zIndex = '9999';
      
      console.log('Drop zone found and made visible:', dropZone);
      console.log('Drop zone classes:', dropZone.className);
      console.log('Drop zone computed opacity:', getComputedStyle(dropZone).opacity);
      
      // Test active state after 2 seconds
      setTimeout(() => {
        dropZone.classList.add('active');
        dropZone.style.transform = 'scale(1.3) translateY(-10px)';
        console.log('Drop zone activated');
      }, 2000);
      
      // Hide after 5 seconds
      setTimeout(() => {
        dropZone.classList.remove('visible', 'active');
        dropZone.style.opacity = '';
        dropZone.style.transform = '';
        console.log('Drop zone hidden');
      }, 5000);
    } else {
      console.error('Drop zone not found!');
    }
  } else {
    console.error('Shop This Drop not initialized!');
  }
};

window.showDropZonePermanently = () => {
  console.log('Making drop zone permanently visible...');
  if (window.shopThisDrop) {
    window.shopThisDrop.ensureCartDropZone();
    const dropZone = document.querySelector('.cart-drop-zone');
    if (dropZone) {
      dropZone.style.opacity = '0.9';
      dropZone.style.transform = 'scale(1) translateY(0)';
      dropZone.style.pointerEvents = 'all';
      dropZone.style.display = 'flex';
      dropZone.style.visibility = 'visible';
      dropZone.style.zIndex = '9999';
      dropZone.classList.add('visible');
      console.log('Drop zone made permanently visible');
    }
  }
};

window.testDragAndDrop = () => {
  console.log('🧪 Testing MOUSE-BASED drag system...');
  
  // Test 1: Check drop zone
  const dropZone = document.querySelector('.cart-drop-zone');
  console.log('1. Drop zone:', dropZone ? '✅ Found' : '❌ Missing');
  
  // Test 2: Check for product cards
  const productCards = document.querySelectorAll('.card-wrapper[data-product-id]');
  console.log('2. Product cards found:', productCards.length);
  
  productCards.forEach((card, i) => {
    const productId = card.dataset.productId;
    const variantId = card.dataset.variantId || card.querySelector('.card-variant-id')?.value;
    console.log(`   Card ${i+1}: Product ${productId}, Variant ${variantId}`);
  });
  
  console.log('📝 Try CLICKING AND DRAGGING a product card to the bottom-left cart zone!');
  console.log('💡 Expected: "MOUSE DRAG started" → "MOUSE Over drop zone" → "MOUSE DROP SUCCESS"');
  console.log('🎨 Visual: Card clone should follow your cursor, drop zone should pulse when hovering');
};

window.forceSimpleDrag = () => {
  console.log('🔧 Forcing simple drag setup...');
  if (window.shopThisDrop) {
    window.shopThisDrop.initDragAndDropCards();
  }
};

window.testCartUI = () => {
  console.log('🧪 Testing cart UI elements...');
  
  // Check for cart count elements
  const cartCountElements = document.querySelectorAll('.cart-count-bubble, .cart-count, [data-cart-count]');
  console.log('Cart count elements found:', cartCountElements.length);
  cartCountElements.forEach((el, i) => {
    console.log(`  Element ${i+1}:`, el.tagName, el.className, 'Current count:', el.textContent);
  });
  
  // Check for cart icons
  const cartIcons = document.querySelectorAll('.cart-icon, [data-cart-icon]');
  console.log('Cart icon elements found:', cartIcons.length);
  
  // Test cart refresh
  if (window.shopThisDrop) {
    console.log('🔄 Testing cart refresh...');
    window.shopThisDrop.refreshShopifyCartUI({ variant_id: 'test', quantity: 1 });
  }
};
